<script setup lang="ts">
import axios from 'axios';
import { onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import GoogleAddressAutocomplete from 'vue3-google-address-autocomplete';
import { API_URL, GOOGLE_MAPS_API_KEY } from '@idem/common/constantes';

type ActividadIn = {
  nombre: string;
  descripcion: string;
  fecha: string; // YYYY-MM-DD
  hora: string; // HH:mm
  direccion?: string;
  latitud?: string; // input as string, converted to number on submit
  longitud?: string; // input as string, converted to number on submit
  idLocal?: string; // optional local id when asociating to a local
};

type LocalOut = {
  id: string;
  idEmpresa: string;
  nombre: string;
  descripcion?: string;
  direccion: string;
  ciudad: string;
  publicado?: boolean;
  horario?: any;
  dias?: any;
  latitud: number;
  longitud: number;
};

const $router = useRouter();
const apiBaseUrl = API_URL || '/api';

const form = {
  nombre: '',
  descripcion: '',
  fecha: '',
  hora: '',
  direccion: '',
  latitud: '',
  longitud: '',
  idLocal: '',
  link: '',
};

const locales = ref<LocalOut[]>([]);

async function cargarLocales() {
  try {
    const res = await axios.get(`${apiBaseUrl}/empresa/locales`);
    locales.value = res.data || [];
  } catch (e) {
    // Silently ignore; dropdown will just be empty
    locales.value = [];
  }
}

function onSeleccionLocal() {
  const local = locales.value.find(l => l.id === form.idLocal);
  if (local) {
    form.direccion = local.direccion;
    form.latitud = String(local.latitud ?? '');
    form.longitud = String(local.longitud ?? '');
  }
}

onMounted(() => {
  cargarLocales();
});

async function submit() {
  try {
    const payload = {
      ...form,
      latitud: form.latitud !== '' ? Number(form.latitud) : undefined,
      longitud: form.longitud !== '' ? Number(form.longitud) : undefined,
    };
    await axios.put(`${apiBaseUrl}/actividades`, payload);
    alert('Actividad agregada correctamente');
    $router.push('/actividades/lista');
  } catch (e: any) {
    alert(e?.response?.data?.message || 'No se pudo agregar la actividad');
  }
}

function asignarCoordenadas(direccion) {
  form.latitud = direccion.geometry.location.lat();
  form.longitud = direccion.geometry.location.lng();
}
</script>

<template>
  <section class="mx-auto max-w-2xl px-4 py-8">
    <router-link to="/actividades/lista" class="mb-6 inline-block text-teal-600 hover:underline"
      >← Volver</router-link
    >
    <h1 class="mb-4 text-2xl font-bold text-gray-900">Agregar Actividad</h1>
    <form class="space-y-4" @submit.prevent="submit">
      <div v-if="locales.length > 0">
        <label class="mb-1 block text-sm font-medium text-gray-700">Local</label>
        <select
          class="w-full rounded border px-3 py-2"
          v-model="form.idLocal"
          @change="onSeleccionLocal"
        >
          <option value="">Selecciona un local (opcional)</option>
          <option v-for="l in locales" :key="l.id" :value="l.id">{{ l.nombre }}</option>
        </select>
        <p class="mt-1 text-xs text-gray-500">
          Si seleccionas un local, se usará su dirección automáticamente.
        </p>
      </div>
      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Nombre</label>
        <input v-model="form.nombre" class="w-full rounded border px-3 py-2" required />
      </div>
      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Descripción</label>
        <textarea v-model="form.descripcion" class="w-full rounded border px-3 py-2" required />
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Fecha</label>
          <input
            type="date"
            v-model="form.fecha"
            class="w-full rounded border px-3 py-2"
            required
          />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Hora</label>
          <input type="time" v-model="form.hora" class="w-full rounded border px-3 py-2" required />
        </div>
      </div>
      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Dirección</label>
        <google-address-autocomplete
          :apiKey="GOOGLE_MAPS_API_KEY || 'NO_API_KEY_PROVIDED'"
          v-model="form.direccion"
          class="w-full rounded border px-3 py-2"
          placeholder="Calle 123, Ciudad"
          @callback="asignarCoordenadas"
        />
      </div>
      <div>
        <label class="mb-1 block text-sm font-medium text-gray-700">Link</label>
        <input type="text" v-model="form.link" class="w-full rounded border px-3 py-2" />
      </div>
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2" v-show="false">
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Latitud</label>
          <input
            v-model="form.latitud"
            inputmode="decimal"
            class="w-full rounded border px-3 py-2"
            placeholder="-33.45"
          />
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gray-700">Longitud</label>
          <input
            v-model="form.longitud"
            inputmode="decimal"
            class="w-full rounded border px-3 py-2"
            placeholder="-70.67"
          />
        </div>
      </div>
      <button type="submit" class="rounded bg-teal-600 px-4 py-2 text-white hover:bg-teal-700">
        Guardar
      </button>
    </form>
  </section>
</template>

<style scoped></style>
